import React, { useRef } from "react";
import { Box } from "@mui/material";
import QueryBuilder from "../../../components/QueryBuilder/Index";

interface MixedSqlEditorProps {
  // Per-instance query value and change handler so each sub-query keeps its own
  // editor state without touching the shared RuleResourceContext value.
  value: string;
  onChange: (value: string) => void;
  // Stable key used by the underlying SQLEditor to persist expansion/cursor
  // across remounts when switching between sub-queries.
  editorStateKey: string;
  label?: string;
  error?: any;
  setErrors: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>;
  onFocus?: () => void;
  onBlur?: () => void;
  // Controls which helper panels appear beside the editor.
  showAvailableVariables?: boolean;
  showResourceColumnsChips?: boolean;
  // Optional external ref so the container can insert columns/variables at the
  // cursor of the currently-active editor.
  editorRef?: React.MutableRefObject<any>;
  // Hides the underlying editor's "Available Columns" section. The Mixed V2
  // editor shows Available Columns in its own side panel, so the in-editor
  // section is redundant; defaults to true here.
  hideAvailableColumns?: boolean;
  // Optional: opens the standalone pop-out editor for this query. When provided,
  // a pop-out button is rendered in the editor toolbar next to the expand toggle.
  onPopOut?: () => void;
}

// Thin wrapper around the shared QueryBuilder/SQLEditor. It supplies custom
// value/onChange/ref props (the "custom state management" path already supported
// by SQLEditor) so the V2 Mixed editor can drive each sub-query's SQL
// independently while reusing the existing Ace editor, Available Columns tabs and
// Available Variables — no changes to the shared editor are required.
const MixedSqlEditor: React.FC<MixedSqlEditorProps> = ({
  value,
  onChange,
  editorStateKey,
  label,
  error,
  setErrors,
  onFocus,
  onBlur,
  showAvailableVariables = true,
  showResourceColumnsChips = false,
  editorRef: externalEditorRef,
  hideAvailableColumns = true,
  onPopOut,
}) => {
  const internalEditorRef = useRef<any>(null);
  const editorRef = externalEditorRef ?? internalEditorRef;

  return (
    <Box className="mixed-v2-editor">
      <QueryBuilder
        // No-op: QueryBuilder has an internal effect that calls handleChange on
        // every shared queryBuilderTempValue change. We drive this editor via the
        // custom value/onChange path instead, so handleChange must be a no-op to
        // avoid touching the sub-query's own state.
        handleChange={() => null}
        setErrors={setErrors}
        error={error}
        mergeQuerycolumns={[]}
        isFinalQuery={false}
        dialogType="mixed"
        label={label ?? "SQL"}
        editorStateKey={editorStateKey}
        onFocus={onFocus}
        onBlur={onBlur}
        showAvailableVariables={showAvailableVariables}
        showResourceColumnsChips={showResourceColumnsChips}
        hideAvailableColumns={hideAvailableColumns}
        showRenderVariables={false}
        customValue={value}
        customOnChange={onChange}
        customEditorRef={editorRef}
        onPopOut={onPopOut}
      />
    </Box>
  );
};

export default MixedSqlEditor;
